import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Divider,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { WarbandMemberDTO, Spell, MemberRole } from '../../types';
import { useWarbandStore } from '../../stores/warbandStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { formatBuffEffect, getBuffName } from '../../utils/formatting';

interface ManageBuffsDebuffsModalProps {
  open: boolean;
  editMode: boolean;
  member: WarbandMemberDTO;
  memberRole: MemberRole;
  onClose: () => void;
}

export const ManageBuffsDebuffsModal: React.FC<ManageBuffsDebuffsModalProps> = ({
  open,
  editMode,
  member,
  memberRole,
  onClose,
}) => {
  const { spellcastersAndSpells } = useWarbandStore();
  const allBuffs = useReferenceDataStore((state) => state.buffs);
  const updateMemberBuffs = useDirtyTrackingStore((s) => s.updateMemberBuffs);

  const [error, setError] = React.useState<string | null>(null);
  const [selectedBuffId, setSelectedBuffId] = React.useState<string | null>(null);

  const warbandSpells: Spell[] = React.useMemo(
    () => spellcastersAndSpells?.warbandSpells ?? [],
    [spellcastersAndSpells?.warbandSpells]
  );
  const warbandSpellIds = React.useMemo(
    () => new Set(warbandSpells.map((s) => s.id)),
    [warbandSpells]
  );

  // Reset selection each time the modal opens
  React.useEffect(() => {
    if (!open) return;
    setSelectedBuffId(null);
    setError(null);
  }, [open, member]);

  const handleSave = () => {
    if (!selectedBuffId) return;
    // Expand existing stacks back to repeated IDs then append the new one
    const existingIds = (member.buffsAndDebuffs ?? []).flatMap((b) =>
      Array(b.stackCount ?? 1).fill(b.id)
    );
    updateMemberBuffs(member.id, memberRole, [...existingIds, selectedBuffId]);
    onClose();
  };

  // Build a map of current stack counts per buff ID
  const activeBuffCounts = new Map<string, number>();
  for (const b of (member.buffsAndDebuffs ?? [])) {
    activeBuffCounts.set(b.id, b.stackCount ?? 1);
  }

  // Exclude warband-only buffs and buffs that are at max stacks
  const availableBuffs = allBuffs.filter((b) => {
    if (b.globalYou > 0 || b.globalThem > 0) return false;
    const currentCount = activeBuffCounts.get(b.id) ?? 0;
    if (currentCount === 0) return true;
    if (b.stacking === 0) return false; // not stackable
    return currentCount < b.stacking; // still room for more stacks
  });
  const warbandSpellBuffs = availableBuffs.filter(
    (b) => (b.source === 'injury' && (editMode && memberRole !== 'soldier')) || (b.source === 'spell' && b.sourceId != null && warbandSpellIds.has(b.sourceId))
  );
  const otherSpellBuffs = availableBuffs.filter(
    (b) => b.source === 'spell' && b.sourceId != null && !warbandSpellIds.has(b.sourceId)
  );
  const customBuffs = availableBuffs.filter((buff) => buff.source === 'custom' || buff.isCustom);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { height: 400 } }}>
      <DialogContent dividers sx={{ overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {warbandSpellBuffs.length === 0 && otherSpellBuffs.length === 0 && customBuffs.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No effects available to add.
          </Typography>
        ) : (
          <Box>
            {warbandSpellBuffs.length > 0 && (
              <>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                  From Your Spells
                </Typography>
                <List dense disablePadding>
                  {warbandSpellBuffs.map((buff) => (
                    <ListItemButton
                      key={buff.id}
                      selected={selectedBuffId === buff.id}
                      onClick={() => setSelectedBuffId(buff.id)}
                      sx={{ borderRadius: 1, py: 0.5 }}
                    >
                      <ListItemText
                        primary={getBuffName(buff)}
                        secondary={formatBuffEffect(buff)}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </>
            )}

            {otherSpellBuffs.length > 0 && (
              <>
                {warbandSpellBuffs.length > 0 && <Divider sx={{ my: 1 }} />}
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                  Other Spells
                </Typography>
                <List dense disablePadding>
                  {otherSpellBuffs.map((buff) => (
                    <ListItemButton
                      key={buff.id}
                      selected={selectedBuffId === buff.id}
                      onClick={() => setSelectedBuffId(buff.id)}
                      sx={{ borderRadius: 1, py: 0.5 }}
                    >
                      <ListItemText
                        primary={getBuffName(buff)}
                        secondary={formatBuffEffect(buff)}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </>
            )}
            {customBuffs.length > 0 && (
              <>
                {(warbandSpellBuffs.length > 0 || otherSpellBuffs.length > 0) && <Divider sx={{ my: 1 }} />}
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                  Custom Effects
                </Typography>
                <List dense disablePadding>
                  {customBuffs.map((buff) => (
                    <ListItemButton
                      key={buff.id}
                      selected={selectedBuffId === buff.id}
                      onClick={() => setSelectedBuffId(buff.id)}
                      sx={{ borderRadius: 1, py: 0.5 }}
                    >
                      <ListItemText
                        primary={getBuffName(buff)}
                        secondary={formatBuffEffect(buff)}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={selectedBuffId === null}
        >
          Add Magic Effect
        </Button>
      </DialogActions>
    </Dialog>
  );
};
